import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Copy, Download, Eye, EyeOff, ShieldCheck, Trash2 } from "lucide-react";
import { API, authHeaders, formatApiError, setToken } from "@/lib/api";
import {
  PERSONAL_FIELDS,
  EMERGENCY_FIELDS,
  TEAM_LABELS,
  SOCIAL_LABELS,
  SIZE_LABELS,
  PRO_LABELS,
  PLATFORM_LABELS,
  BANKING_FIELDS,
  BRAND_GROWTH_FIELDS,
  DOC_TYPE_LABELS,
  STATUS_STYLES,
} from "@/lib/labels";

const Item = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-mono uppercase tracking-[0.15em] text-slate-500">{label}</p>
    <p className="text-sm text-slate-200 mt-0.5 break-words">{value || "—"}</p>
  </div>
);

const Section = ({ title, testid, children }) => (
  <div className="xy-card p-5 md:p-6" data-testid={testid}>
    <p className="font-display font-semibold text-slate-100 mb-4">{title}</p>
    {children}
  </div>
);

const copyText = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch {
    toast.error("Copy failed");
  }
};

const ProRow = ({ orgKey, label, creds }) => {
  const [show, setShow] = useState(false);
  const empty = !creds?.username && !creds?.password;
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4" data-testid={`admin-pro-${orgKey}`}>
      <p className="text-xs font-mono uppercase tracking-[0.18em] text-violet-300 mb-3">{label}</p>
      {empty ? (
        <p className="text-sm text-slate-600">Not provided</p>
      ) : (
        <div className="space-y-2 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-400">Username</span>
            <span className="flex items-center gap-2 text-slate-200">
              <span className="font-mono text-xs">{creds.username || "—"}</span>
              {creds.username ? (
                <button data-testid={`btn-copy-${orgKey}-username`} onClick={() => copyText(creds.username, "Username")} className="text-slate-500 hover:text-violet-300 transition-colors" aria-label="Copy username">
                  <Copy size={13} />
                </button>
              ) : null}
            </span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-slate-400">Password</span>
            <span className="flex items-center gap-2 text-slate-200">
              <span className="font-mono text-xs" data-testid={`text-${orgKey}-password`}>
                {creds.password ? (show ? creds.password : "••••••••") : "—"}
              </span>
              {creds.password ? (
                <>
                  <button data-testid={`btn-show-${orgKey}-password`} onClick={() => setShow((s) => !s)} className="text-slate-500 hover:text-violet-300 transition-colors" aria-label="Toggle password">
                    {show ? <EyeOff size={13} /> : <Eye size={13} />}
                  </button>
                  <button data-testid={`btn-copy-${orgKey}-password`} onClick={() => copyText(creds.password, "Password")} className="text-slate-500 hover:text-violet-300 transition-colors" aria-label="Copy password">
                    <Copy size={13} />
                  </button>
                </>
              ) : null}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminSubmissionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/submissions/${id}`, { headers: authHeaders(), withCredentials: true });
        setSub(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          setToken(null);
          navigate("/admin/login");
        } else if (err?.response?.status === 404) {
          setNotFound(true);
        } else {
          toast.error(formatApiError(err));
        }
      }
    })();
  }, [id, navigate]);

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${API}/admin/submissions/${id}`, { status }, { headers: authHeaders(), withCredentials: true });
      setSub((s) => ({ ...s, status }));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/admin/submissions/${id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Submission deleted");
      navigate("/admin");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const download = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/files/${doc.storage_path}`, {
        headers: authHeaders(),
        responseType: "blob",
        withCredentials: true,
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#08090E] flex items-center justify-center" data-testid="admin-not-found">
        <div className="text-center">
          <p className="font-display text-xl font-semibold text-slate-300">Submission not found</p>
          <Link to="/admin" className="text-sm text-violet-300 hover:underline mt-3 inline-block">Back to roster</Link>
        </div>
      </div>
    );
  }

  if (!sub) {
    return (
      <div className="min-h-screen bg-[#08090E] flex items-center justify-center">
        <p className="text-sm text-slate-500" data-testid="admin-detail-loading">Loading...</p>
      </div>
    );
  }

  const filledTeam = Object.entries(TEAM_LABELS).filter(([k]) => sub.team?.[k]?.name);
  const filledSocials = Object.entries(SOCIAL_LABELS).filter(([k]) => sub.socials?.[k]);
  const filledSizes = Object.entries(SIZE_LABELS).filter(([k]) => sub.sizes?.[k]);

  return (
    <div className="min-h-screen bg-[#08090E]">
      <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/admin" data-testid="link-back-to-roster" className="text-slate-500 hover:text-violet-300 transition-colors">
              <ArrowLeft size={18} />
            </Link>
            <div className="min-w-0">
              <p className="xy-eyebrow">Client file</p>
              <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight truncate" data-testid="admin-detail-artist-name">
                {sub.artist_name}
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {["new", "reviewed", "archived"].map((s) => (
              <button
                key={s}
                data-testid={`btn-set-status-${s}`}
                onClick={() => updateStatus(s)}
                className={`rounded-full border px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-all ${
                  sub.status === s ? STATUS_STYLES[s] : "border-white/10 text-slate-500 hover:text-slate-300"
                }`}
              >
                {s}
              </button>
            ))}
            <button
              data-testid="btn-delete-submission"
              onClick={remove}
              className="rounded-full border border-white/10 p-2 text-slate-500 hover:border-red-400/40 hover:text-red-300 transition-colors"
              aria-label="Delete submission"
            >
              <Trash2 size={14} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title="Brand + Growth" testid="section-brand-growth">
          <div className="grid grid-cols-1 gap-4">
            {BRAND_GROWTH_FIELDS.map(([k, label]) => (
              <Item key={k} label={label} value={sub.brand_growth?.[k]} />
            ))}
          </div>
        </Section>

        <Section title="Personal & Legal" testid="section-personal">
          <div className="grid grid-cols-2 gap-4">
            {[...PERSONAL_FIELDS, ...EMERGENCY_FIELDS].map(([k, label]) => (
              <Item key={k} label={label} value={sub.personal?.[k]} />
            ))}
            <Item label="Signature" value={sub.signature} />
            <Item label="Submitted" value={sub.created_at ? new Date(sub.created_at).toLocaleString() : ""} />
          </div>
        </Section>

        <Section title={`Team (${filledTeam.length})`} testid="section-team">
          {filledTeam.length === 0 ? (
            <p className="text-sm text-slate-600">No team contacts provided.</p>
          ) : (
            <div className="space-y-4">
              {filledTeam.map(([k, label]) => (
                <div key={k} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <p className="text-xs font-mono uppercase tracking-[0.18em] text-violet-300 mb-2">{label}</p>
                  <p className="text-sm text-slate-200">{sub.team[k].name}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {[sub.team[k].email, sub.team[k].phone].filter(Boolean).join(" · ") || "No contact info"}
                  </p>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title={`Socials (${filledSocials.length})`} testid="section-socials">
          <div className="grid grid-cols-2 gap-4">
            {filledSocials.map(([k, label]) => (
              <Item key={k} label={label} value={sub.socials[k]} />
            ))}
            {filledSocials.length === 0 && <p className="text-sm text-slate-600">None provided.</p>}
          </div>
        </Section>

        <Section title={`Sizes (${filledSizes.length})`} testid="section-sizes">
          <div className="grid grid-cols-3 gap-4">
            {filledSizes.map(([k, label]) => (
              <Item key={k} label={label} value={sub.sizes[k]} />
            ))}
            {filledSizes.length === 0 && <p className="text-sm text-slate-600">None provided.</p>}
          </div>
        </Section>

        <Section title="PRO Credential Vault" testid="section-pro-vault">
          <div className="flex items-center gap-2 mb-4 text-xs text-slate-500">
            <ShieldCheck size={14} className="text-amber-300" />
            Confidential — handle with care
          </div>
          <div className="space-y-3">
            {Object.entries(PRO_LABELS).map(([k, label]) => (
              <ProRow key={k} orgKey={k} label={label} creds={sub.pro_logins?.[k]} />
            ))}
          </div>
        </Section>

        <Section title="Platform Access" testid="section-platform-access">
          <div className="space-y-3">
            {Object.entries(PLATFORM_LABELS).map(([k, label]) => (
              <ProRow key={k} orgKey={k} label={label} creds={sub.platforms?.[k]} />
            ))}
          </div>
        </Section>

        <Section title="Banking (Royalty Payouts)" testid="section-banking">
          <div className="grid grid-cols-2 gap-4">
            {BANKING_FIELDS.map(([k, label]) => (
              <Item key={k} label={label} value={sub.banking?.[k]} />
            ))}
          </div>
        </Section>

        <Section title={`Documents (${sub.documents?.length || 0})`} testid="section-documents">
          {(sub.documents || []).length === 0 ? (
            <p className="text-sm text-slate-600">No documents uploaded.</p>
          ) : (
            <div className="space-y-3">
              {sub.documents.map((doc) => (
                <div key={doc.storage_path} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
                  <div className="min-w-0">
                    <p className="text-xs font-mono uppercase tracking-[0.15em] text-violet-300">
                      {DOC_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                    </p>
                    <p className="text-sm text-slate-300 truncate mt-1">{doc.original_filename}</p>
                  </div>
                  <button
                    data-testid={`btn-download-${doc.doc_type}`}
                    onClick={() => download(doc)}
                    className="inline-flex items-center gap-1.5 rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-300 hover:bg-violet-500/20 transition-colors shrink-0"
                  >
                    <Download size={13} /> Download
                  </button>
                </div>
              ))}
            </div>
          )}
        </Section>
      </main>
    </div>
  );
}
